/**
 * Subtracts voluntary adjustments from total adjustments for each key.
 * 
 * @param {object} voluntary - An object containing voluntary adjustments.
 * @param {object} totals - An object containing total adjustments.
 * 
 * @returns {object} - A new object containing the differences between 
 * voluntary and total adjustments for each key that exists in both objects. 
 * Keys that only exist in `voluntary` are also copied to the result.
 */
const subtractAdjustments = (voluntary, totals) => ({
  GST: totals.GST - voluntary.GST,
  HST: totals.HST - voluntary.HST,
  PST: totals.PST - voluntary.PST,
  RST: totals.RST - voluntary.RST,
  net: totals.net - voluntary.net,
  total: totals.total - voluntary.total
});

/**
 * Removes voluntaryBenefit adjustments from an invoice. This is because voluntaryBenefits are employee paid, but they are still included in the account level invoices which can cause some confusion.
 * 
 * This function iterates through invoice adjustments and user adjustments. 
 * For each adjustment:
 *  - If the voluntary adjustment total is not zero, it subtracts the 
 *    voluntary adjustments from the total adjustments
 *  - It then updates the corresponding adjustment object in the invoice.
 * 
 * @param {object} invoice - The invoice object to be modified.
 * The invoice object is expected to have an `adjustments` property 
 * containing an array of adjustment objects, and a `currentTransactions` 
 * property with a `users` property containing user objects. Each user 
 * object should have an `amounts` property with properties for 
 * `voluntaryBenefits.adjustments` and `totals.adjustments`.
 * 
 * @returns {object} - The modified invoice object with voluntary 
 * adjustments removed.
 */
export const removeVoluntaryAdjustmentsAndPremiums = invoice => {
  if (invoice?.adjustments?.length) {
    for (let adjustment of invoice.adjustments) {
      const voluntary = adjustment?.amounts?.voluntaryBenefits?.adjustments;
      const totals = adjustment?.amounts?.totals?.adjustments;
      if (voluntary && totals) {
        if (voluntary.total !== 0) {
          // Update adjustment totals
          adjustment.amounts.totals.adjustments = subtractAdjustments(voluntary, totals);

          // Update the Invoice Summary
          invoice.summary.overallSummary.totals.GST -= voluntary.GST;
          invoice.summary.overallSummary.totals.HST -= voluntary.HST;
          invoice.summary.overallSummary.totals.PST -= voluntary.PST;
          invoice.summary.overallSummary.totals.RST -= voluntary.RST;
        }
        invoice.currentTransactions.divisions[adjustment.meta.divisionID].totals.GST -= voluntary.GST;
        invoice.currentTransactions.divisions[adjustment.meta.divisionID].totals.PST -= voluntary.PST;
        invoice.currentTransactions.divisions[adjustment.meta.divisionID].totals.HST -= voluntary.HST;
        invoice.currentTransactions.divisions[adjustment.meta.divisionID].totals.RST -= voluntary.RST;
      }
    }
  }
  for (const user of Object.values(invoice.currentTransactions.users)) {
    let taxAndAdjustmentsUpdated = false;
    if (user?.amounts?.voluntaryBenefits?.adjustments?.total && user.amounts.voluntaryBenefits.adjustments.total !== 0) {
      user.amounts.totals.adjustments = subtractAdjustments(user.amounts.voluntaryBenefits.adjustments, user.amounts.totals.adjustments);
      user.amounts.totals.GST -= user.amounts.voluntaryBenefits.adjustments.GST;
      user.amounts.totals.HST -= user.amounts.voluntaryBenefits.adjustments.HST;
      user.amounts.totals.RST -= user.amounts.voluntaryBenefits.adjustments.RST;
      user.amounts.totals.PST -= user.amounts.voluntaryBenefits.adjustments.PST;
      taxAndAdjustmentsUpdated = true;
    }
    if (user.amounts.totals.voluntaryBenefitsGrandTotal !== 0) {
      user.amounts.totals.grandTotal -= user.amounts.totals.voluntaryBenefitsGrandTotal;
      user.amounts.totals.premium -= user.amounts.voluntaryBenefits.premium;
      user.amounts.totals.GST -= user.amounts.voluntaryBenefits.GST;
      user.amounts.totals.HST -= user.amounts.voluntaryBenefits.HST;
      user.amounts.totals.RST -= user.amounts.voluntaryBenefits.RST;
      user.amounts.totals.PST -= user.amounts.voluntaryBenefits.PST;
      taxAndAdjustmentsUpdated = true;
    }
    if (taxAndAdjustmentsUpdated) {
      // If taxes and adjustments were updated, reset taxes to 0
      user.amounts.voluntaryBenefits.GST = 0;
      user.amounts.voluntaryBenefits.PST = 0;
      user.amounts.voluntaryBenefits.RST = 0;
      user.amounts.voluntaryBenefits.HST = 0;
      user.amounts.voluntaryBenefits.adjustments = {
        GST: 0,
        PST: 0,
        RST: 0,
        HST: 0,
        net: 0
      };
    }
  }
  if (invoice.summary.divisionSummaries) {
    for (const division of invoice.summary.divisionSummaries) {
      if (division.voluntaryBenefits?.adjustments && division?.voluntaryBenefits?.adjustments?.total !== 0) {
        division.totals.GST -= division.voluntaryBenefits?.adjustments?.GST || 0;
        division.totals.HST -= division.voluntaryBenefits?.adjustments?.HST || 0;
        division.totals.RST -= division.voluntaryBenefits?.adjustments?.RST || 0;
        division.totals.PST -= division.voluntaryBenefits?.adjustments?.PST || 0;
        if (division?.voluntaryBenefits?.adjustments && Object.keys(division?.voluntaryBenefits?.adjustments).length) {
          division.voluntaryBenefits.GST = 0;
          division.voluntaryBenefits.PST = 0;
          division.voluntaryBenefits.RST = 0;
          division.voluntaryBenefits.HST = 0;
          division.voluntaryBenefits.adjustments.GST = 0;
          division.voluntaryBenefits.adjustments.PST = 0;
          division.voluntaryBenefits.adjustments.RST = 0;
          division.voluntaryBenefits.adjustments.HST = 0;
        }
      }
    }
  }
  return invoice;
};